import { defineStore } from 'pinia';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';
import { CounterpartyEndPoint } from '@/modules/counterparty/enums/counterparty-end-point.enum.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';

type RawCounterpartyRowResponse = { data: ItemsDto<Record<string, unknown>> };

type CounterpartyRowState = {
  item: Record<string, unknown>;
  isLoading: boolean;
  controller: AbortController | null;
};

export const useCounterpartyRowStore = defineStore(StoreId.CounterpartyRow, {
  state: (): CounterpartyRowState => ({
    item: {},
    isLoading: false,
    controller: null,
  }),
  actions: {
    async fetch(clientId: number) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.isLoading = true;

      try {
        const response = await GatewayApi.execute<RawCounterpartyRowResponse>(CounterpartyEndPoint.GetList, {
          filter: GatewayFilter.and(GatewayFilter.eq('CLIENT_ID', clientId)),
          limit: 1,
          signal: controller.signal,
          mapData: false,
        });

        if (this.controller !== controller) {
          return;
        }

        this.item = response.data.items[0] || {};
      } catch (error) {
        if (this.controller !== controller) {
          return;
        }
        if (ErrorUtil.isForbidden(error) || ErrorUtil.isUnexpected(error)) {
          throw error;
        }
      } finally {
        if (this.controller === controller) {
          this.isLoading = false;
          this.controller = null;
        }
      }
    },
    async fetchOnly(clientId: number): Promise<Record<string, unknown>> {
      const item = await GatewayApi.getOne<Record<string, unknown>>(
        CounterpartyEndPoint.GetList,
        GatewayFilter.and(GatewayFilter.eq('CLIENT_ID', clientId))!
      );

      if (!item) {
        throw new Error('Entity not found');
      }

      return item;
    },
    abort() {
      this.controller?.abort();
      this.isLoading = false;
      this.controller = null;
    },
    clear() {
      this.item = {};
    },
  },
});
